/**
 * Footer Lock Composable
 *
 * Provides functionality to temporarily hide the footer during critical loading phases
 * to prevent layout shifts. Multiple components can lock the footer simultaneously.
 */
import { type Ref, ref, readonly } from 'vue'

interface FooterLockState {
    footerLocked: Readonly<Ref<boolean>>
    lockReason: Readonly<Ref<string>>
    lockingComponents: Readonly<Ref<ReadonlySet<string>>>
}

// Global state - shared across all composable instances
const footerLocked = ref(false)
const lockReason = ref('')
const lockingComponents = ref(new Set<string>())

export function useFooterLock(): FooterLockState & {
    lockFooter: (componentId: string, reason?: string) => void
    unlockFooter: (componentId: string) => void
    } {
    /**
     * Lock the footer to prevent layout shifts during content loading
     * @param componentId - Unique identifier for the component locking the footer
     * @param reason - Optional reason for the lock (for debugging)
     */
    const lockFooter = (componentId: string, reason = 'loading'): void => {
        lockingComponents.value.add(componentId)
        footerLocked.value = true
        lockReason.value = reason

        if (import.meta.client) {
            console.debug(`[FooterLock] Locked by: ${componentId} (${reason})`)
        }
    }

    /**
     * Unlock the footer for a specific component
     * Footer will only be unlocked when all locking components have released their locks
     * @param componentId - Unique identifier for the component unlocking the footer
     */
    const unlockFooter = (componentId: string): void => {
        lockingComponents.value.delete(componentId)

        if (lockingComponents.value.size === 0) {
            footerLocked.value = false
            lockReason.value = ''

            if (import.meta.client) {
                console.debug(`[FooterLock] Unlocked - all components released`)
            }
        } else {
            if (import.meta.client) {
                console.debug(`[FooterLock] Partially unlocked by: ${componentId}, remaining locks:`, Array.from(lockingComponents.value))
            }
        }
    }

    return {
        lockFooter,
        unlockFooter,
        footerLocked: readonly(footerLocked),
        lockReason: readonly(lockReason),
        lockingComponents: readonly(lockingComponents)
    }
}